<script lang="ts">
import { inject, onBeforeUnmount, ref, type Ref } from "vue";
import useNumber from "@/composable/components/number";
import useText from "@/composable/components/text";
import services from "@/composable/services";
import { HttpStatusCodes } from "@/utils/HttpUtils";
import { Form } from "vee-validate";
import TextField from "@/components/forms/TextField.vue";
import { BButton, BNotification, BStepItem, BSteps } from "buefy";
import { useI18n } from "vue-i18n";
import { CreateUserRequest } from "@/types/generated/UsedGeneratedTypes";
import { ErrorType } from "@/composable/application/errors";
import type { VeeForm } from "@/types/vee-validate";
import { useFieldErrorReset } from "@/composable/forms/useFieldErrorReset";
import { useCrossFieldConfirmation } from "@/composable/forms/useCrossFieldConfirmation";

/**
 * Cooldown anti-spam sur le lien "Renvoyer une nouvelle clé par email" .
 * 10 s = compromis : assez court pour ne pas frustrer un utilisateur qui
 * a vraiment besoin de renvoyer ( ex : mail perdu , spam folder ) , assez
 * long pour eviter qu'un clic accidentel double-tap ne genere un spam SMTP
 * sur le backend . Aligne sur la pratique courante des UI de reset password .
 */
const RESEND_COOLDOWN_SECONDS = 10;

export default {
  emit: ["changeState"],
  components: { BNotification, BSteps, BStepItem, BButton, TextField, Form },
  props: {
    signIn: {
      required: true,
      type: Function,
    },
  },
  setup(props) {
    const { refNumber: activeStep } = useNumber();
    const { refText: newPassword } = useText();
    const { refText: newPasswordConfirm } = useText();
    // Type-cast explicite des refs injectees : sans ca , {@code inject("X")}
    // retourne {@code unknown} et le compilateur refuse les acces .value /
    // les passages a {@code watch()} ( TS2769 / TS18046 ) . Le contrat
    // d'injection est etabli par le parent {@code Signin.vue} qui appelle
    // {@code provide("email", ref<string>(...))} etc .
    const login = inject("login") as Ref<string>;
    const email = inject("email") as Ref<string>;
    const password = inject("password") as Ref<string>;
    const verificationKey = inject("verificationKey") as Ref<string>;
    const { t } = useI18n();
    /**
     * Indicateur "envoi backend en cours" - meme semantique que la phase
     * {@code SENDING} d'EditEmail . Affiche un spinner sur le bouton
     * next/finish et le desactive , empechant tout double-clic ou
     * navigation prematuree . Reste {@code true} jusqu'a reception de
     * la reponse backend ( succes ou echec ) .
     */
    const isSending = ref(false);
    /**
     * Ref vers la balise {@code <Form ref="observer">} de vee-validate .
     * Permet d'appeler {@code setFieldError(field, message)} pour marquer
     * le champ "verificationKey" en rouge depuis la reponse backend 412/422
     * ( cle invalide ) , et de le clearer ( {@code undefined} ) lors du retype
     * pour faire disparaitre l'indicateur d'erreur visuel .
     */
    const observer = ref<VeeForm | null>(null);
    /**
     * Secondes restantes avant que le lien "Renvoyer une nouvelle clé par
     * email" soit a nouveau cliquable . Decrement chaque seconde par
     * {@code cooldownInterval} . {@code 0} = clickable .
     */
    const resendCooldown = ref(0);
    let cooldownInterval: ReturnType<typeof setInterval> | null = null;

    function startCooldown() {
      resendCooldown.value = RESEND_COOLDOWN_SECONDS;
      if (cooldownInterval) clearInterval(cooldownInterval);
      cooldownInterval = setInterval(() => {
        resendCooldown.value -= 1;
        if (resendCooldown.value <= 0) {
          if (cooldownInterval) {
            clearInterval(cooldownInterval);
            cooldownInterval = null;
          }
          resendCooldown.value = 0;
        }
      }, 1000);
    }

    onBeforeUnmount(() => {
      if (cooldownInterval) clearInterval(cooldownInterval);
    });

    // Reset auto des erreurs server-side des qu'un champ change : evite
    // que les messages des tentatives precedentes persistent visuellement
    // alors que les champs sont corriges ( "cle invalide" reste rouge
    // apres correction tant qu'un nouveau submit n'a pas round-trip ) .
    useFieldErrorReset(verificationKey, observer, "missing-password.verificationKey");
    useFieldErrorReset(newPassword, observer, "missing-password.new-pwd");
    useFieldErrorReset(newPasswordConfirm, observer, "missing-password.new-pwd-confirm");
    // Cross-field : modifier le nouveau mot de passe doit re-valider le
    // champ confirm ( la regle vee-validate confirmed ne se redeclenche
    // que quand le confirm change , pas la source ) .
    useCrossFieldConfirmation(newPassword, observer, "missing-password.new-pwd-confirm");

    /**
     * Renvoi d'une nouvelle clé de validation par email .
     *
     * <p>Appel direct au backend avec un payload step-1 ( email seul ,
     * verificationKey / newPassword vides ) pour declencher
     * {@code sendValidationKey} sans tenter de valider l'eventuelle
     * cle saisie par l'utilisateur . Resultat : un nouveau mail est
     * envoye sans bouger d'etape ( reste sur step 2 ) .
     *
     * <p>Cooldown 10 s applique apres chaque appel reussi ou en
     * erreur , pour eviter le spam SMTP par double-clic accidentel
     * ou rage-click sur erreur reseau .
     */
    async function resendValidationKey() {
      if (resendCooldown.value > 0 || isSending.value) return;
      isSending.value = true;
      try {
        await services.loginService.modifAcount({
          email: email.value,
          verificationKey: "",
          newPassword: "",
          newPasswordConfirm: "",
        } as CreateUserRequest);
        services.alertService.toastSuccess(t("alert.new-validation-key-sent"));
        // Clear visuel : la nouvelle cle reçue ne sera plus a comparer
        // a l'ancienne saisie ; on enleve le rouge .
        observer.value?.setFieldError("missing-password.verificationKey", undefined);
        startCooldown();
      } catch (errorsUnknown: unknown) {
        const errors = errorsUnknown as ErrorType | undefined;
        services.alertService.toastServerError(errors as ErrorType);
        // Cooldown applique meme en erreur : si le mail n'est pas parti
        // ( SMTP 503 par exemple ) , l'utilisateur ne doit pas pouvoir
        // marteler le bouton .
        startCooldown();
      }
      finally {
        isSending.value = false;
      }
    }

    // ----------------------------------------------------------------- //
    //  Helpers internes pour testLostPassword (SRP - chaque helper a    //
    //  une responsabilite unique , facile a lire et a tester )          //
    // ----------------------------------------------------------------- //

    /**
     * Valide cote client tous les champs step 2 AVANT tout appel backend .
     * Retourne {@code true} si OK , {@code false} si bloque ( toast +
     * setFieldError applique en interne , submit interrompu ) .
     *
     * <p>Ordre des verifications ( priorite UX - du plus critique au
     * moins critique ) :
     * <ol>
     *   <li><b>vk vide</b> -> toast "Veuillez renseigner la cle" .
     *       <b>Critique</b> : sans cle , le backend
     *       {@code dispatchForgotPassword} verrait hasKey=false et
     *       routerait vers le path "step 1" qui ENVERRAIT UN MAIL .
     *       Or l'utilisateur n'a pas demande de renvoi - le bouton
     *       Valider doit UNIQUEMENT valider , jamais envoyer de mail
     *       ( renvoi = lien dedie "Renvoyer une nouvelle clé par email"
     *       a droite du champ ) ;</li>
     *   <li><b>np ou npc vide</b> -> toast "Veuillez renseigner un
     *       nouveau mot de passe et le confirmer" + inline rouge sur
     *       chaque vide ;</li>
     *   <li><b>np != npc</b> -> toast "Le nouveau mot de passe et sa
     *       confirmation ne correspondent pas" ( l'inline rouge sur le
     *       confirm est deja gere par la regle vee-validate
     *       {@code confirmed} ) . En PRIORITE sur le test backend de
     *       la cle pour eviter qu'un toast "cle invalide" masque le
     *       vrai probleme cote utilisateur ;</li>
     * </ol>
     *
     * <p>Si tous les checks passent , l'appel backend peut tester la cle
     * de validation . Sinon , aucun appel reseau n'est emis .
     */
    function validateStep2Inputs(vk: string, np: string, npc: string): boolean {
      if (!vk) {
        services.alertService.toastErrorMessage(t("login.lost-pwd.verification-key-required-toast"));
        observer.value?.setFieldError(
          "missing-password.verificationKey",
          t("login.lost-pwd.verification-key-required-inline"),
        );
        return false;
      }
      const npMissing = !np;
      const npcMissing = !npc;
      if (npMissing || npcMissing) {
        services.alertService.toastErrorMessage(t("alert.password-required"));
        if (npMissing) {
          observer.value?.setFieldError(
            "missing-password.new-pwd",
            t("login.lost-pwd.new-pwd-required-inline"),
          );
        }
        if (npcMissing) {
          observer.value?.setFieldError(
            "missing-password.new-pwd-confirm",
            t("login.lost-pwd.new-pwd-confirm-required-inline"),
          );
        }
        return false;
      }
      if (np !== npc) {
        services.alertService.toastErrorMessage(t("alert.passwords-do-not-match"));
        return false;
      }
      return true;
    }

    /**
     * Construit le payload PUT /users pour l'endpoint unique
     * {@code services.loginService.modifAcount} .
     *
     * - step 1 ( email seul ) : FORCE vk/np/npc a "" pour eviter qu'apres
     *   step 2 -> echec -> back -> step 1 , la vk residuelle ne fasse
     *   dispatcher le backend sur "step 2" ( cle invalide ) au lieu
     *   d'envoyer un nouveau mail .
     * - step 2 : payload complet pour validation + changement de mdp .
     */
    function buildPayload(isStep1: boolean, emailVal: string, vk: string,
                          np: string, npc: string): CreateUserRequest {
      return {
        email: emailVal,
        verificationKey: isStep1 ? "" : vk,
        newPassword: isStep1 ? "" : np,
        newPasswordConfirm: isStep1 ? "" : npc,
      } as CreateUserRequest;
    }

    /**
     * Gere l'erreur retournee par le backend selon l'etape active .
     *
     * - step 1 + 412/401 : "Email invalide" ( email inconnu en base )
     * - step 2 + 412/401/422 : "Cle de validation invalide" + setFieldError
     *   sur le champ vk pour marquer en rouge ( clear auto au retype via
     *   {@link useFieldErrorReset} )
     * - autre : toast serveur generique ( 503 SMTP , 500 , etc )
     */
    function handleSubmitError(isStep1: boolean, errors: ErrorType | undefined): void {
      const code = errors?.httpResponseCode;
      const isAuthError = code === HttpStatusCodes.PRECONDITION_FAILED
          || code === HttpStatusCodes.UNAUTHORIZED;
      const isValidationError = isAuthError || code === HttpStatusCodes.UNPROCESSABLE_ENTITY;

      if (isStep1 && isAuthError) {
        services.alertService.toastErrorMessage(t("alert.invalid-email"));
      }
      else if (!isStep1 && isValidationError) {
        services.alertService.toastErrorMessage(t("alert.invalid-validation-key-toast"));
        observer.value?.setFieldError(
          "missing-password.verificationKey",
          t("alert.invalid-validation-key"),
        );
      }
      else {
        services.alertService.toastServerError(errors as ErrorType);
      }
    }

    const testLostPassword = async function (
      emailVal: string,
      verificationKeyVal: string,
      newPasswordVal: string,
      newPasswordConfirmVal: string,
    ) {
      const isOnStep1 = activeStep.value === 0;

      // Validation client-side step 2 : bloque le submit en amont si la
      // cle est vide ( eviterait l'envoi accidentel d'un mail ) , si les
      // mots de passe sont vides , ou s'ils ne correspondent pas . Evite
      // un round-trip backend qui pourrait soit envoyer un mail soit
      // retourner une erreur peu claire ( cf validateStep2Inputs javadoc ) .
      if (!isOnStep1 && !validateStep2Inputs(verificationKeyVal, newPasswordVal, newPasswordConfirmVal)) {
        return;
      }

      // Synchronise le password injecte ( utilise par signIn() post-success )
      // avec la valeur saisie quand on est sur step 2 . En step 1 , on ne
      // touche pas a password.value ( pas de mot de passe a synchroniser ) .
      if (!isOnStep1
          && newPasswordVal !== null
          && newPasswordVal === newPasswordConfirmVal) {
        password.value = newPasswordVal;
      }

      const payload = buildPayload(isOnStep1, emailVal,
          verificationKeyVal, newPasswordVal, newPasswordConfirmVal);
      const isPasswordChangeStep = !isOnStep1;

      isSending.value = true;
      try {
        // Step 2 forgot-password : le payload n'a que { email , verificationKey ,
        // newPassword , newPasswordConfirm } - aucun login car l'utilisateur ne
        // l'a jamais saisi ( la modal est email-only en step 1 ) . La reponse
        // CreateUserResult contient le login du compte trouve cote backend ;
        // on le propage dans la ref injectee pour que le signIn() qui suit
        // utilise le bon login plutot qu'une chaine vide ( sinon : 401
        // "username or password incorrect" malgre un changement mdp reussi ) .
        const result = await services.loginService.modifAcount(payload);
        services.alertService.toastSuccess(
          t(isPasswordChangeStep ? "alert.password-updated" : "alert.validation-key-sent"),
        );
        if (isPasswordChangeStep) {
          if (result && result.login && login) {
            login.value = result.login;
          }
          await props.signIn();
        }
        else {
          // Step 1 succes ( backend mail SMTP envoye ) : on bascule
          // SEULEMENT MAINTENANT sur l'etape 2 . Garantie "step 2 affichee
          // <=> mail SMTP a ete accepte" .
          activeStep.value = 1;
        }
      }
      catch (errorsUnknown: unknown) {
        handleSubmitError(isOnStep1, errorsUnknown as ErrorType | undefined);
      }
      finally {
        isSending.value = false;
      }
    };
    return {
      newPassword,
      newPasswordConfirm,
      verificationKey,
      login,
      email,
      password,
      activeStep,
      isSending,
      observer,
      resendCooldown,
      testLostPassword,
      resendValidationKey,
    };
  },
};
</script>

<template>
  <Form
    ref="observer"
    class="formPasswordLost"
  >
    <b-steps
      v-model="activeStep"
      :animated="true"
      :has-navigation="true"
      :rounded="true"
      label-position="bottom"
    >
      <b-step-item
        :clickable="false"
        :label="$t('login.profil')"
        step="1"
      >
        <div
          class="step-content"
          style="margin-top: 1.5%;"
        >
          <h1 class="titleModal has-text-centered step-1-title">
            {{ $t("login.lost-pwd.title-step-1") }}
          </h1>
          <b-notification
            class="notificationPasswordLost border-notif-info-light"
            :aria-close-label="$t('message.close')"
            type="is-info is-light"
          >
            <span
              class="column no-break"
              v-html="$t('login.lost-pwd.info-recovery-step-1')"
            />
          </b-notification>
          <div class="step-1-email-wrapper">
            <TextField
              v-model="email"
              rules="email"
              label="login.email"
              name="missing-password.email"
              place-holder="login.email-placeholder"
              :mandatory="true"
              @update:value="email = $event"
            />
          </div>
        </div>
      </b-step-item>

      <b-step-item
        :clickable="false"
        :label="$t('login.pwd')"
        step="2"
      >
        <h1 class="titleModal has-text-centered">
          {{ $t("login.lost-pwd.title-step-2") }}
        </h1>
        <b-notification
          v-if="email"
          class="notificationPasswordLost border-notif-info-light"
          :aria-close-label="$t('message.close')"
          type="is-info is-light"
        >
          <span
            class="column no-break"
            v-html="$t('login.lost-pwd.info-mail-validate-key', { email })"
          />
        </b-notification>
        <!-- Slot {@code right} = lien "Renvoyer une nouvelle clé" colle a
             droite de l'input ( hors zone .help , donc TOUJOURS visible ,
             pas conditionne par une erreur de validation ) . Disabled
             pendant le cooldown 10 s ou un envoi en cours . -->
        <TextField
          v-model="verificationKey"
          rules="length:12"
          label="login.lost-pwd.validation-key"
          name="missing-password.verificationKey"
          place-holder="login.verificationKey-placeholder"
          :mandatory="true"
          horizontal
          @update:value="verificationKey = $event"
        >
          <template #right>
            <a
              class="resend-key-link"
              :class="{ 'is-disabled': resendCooldown > 0 || isSending }"
              :aria-disabled="resendCooldown > 0 || isSending"
              @click.prevent="resendValidationKey()"
            >
              {{ resendCooldown > 0
                ? $t('login.lost-pwd.resend-cooldown', { sec: resendCooldown })
                : $t('login.resend-validation-key') }}
            </a>
          </template>
        </TextField>
        <TextField
          v-model="newPassword"
          type="password"
          label="login.lost-pwd.new-pwd"
          name="missing-password.new-pwd"
          place-holder="login.pwd-placeholder"
          :mandatory="true"
          horizontal
          @update:value="newPassword = $event"
        />
        <TextField
          v-model="newPasswordConfirm"
          :disabled="newPassword === null"
          rules="confirmed:missing-password.new-pwd"
          type="password"
          label="login.lost-pwd.new-pwd-confirm"
          name="missing-password.new-pwd-confirm"
          place-holder="login.pwd-placeholder"
          :mandatory="true"
          horizontal
          @update:value="newPasswordConfirm = $event"
        />
      </b-step-item>
      <template #navigation="{ previous, next }">
        <b-button
          aria-description="missing-password.toSecondStep"
          :disabled="previous.disabled"
          icon-right="chevron-left"
          outlined
          @click.prevent="previous.action"
        />
        <b-button
          class="btnStepNext"
          :loading="isSending"
          :disabled="isSending || (
            activeStep === 1
              ? verificationKey === '' && newPassword === '' && newPasswordConfirm === ''
              : email === ''
          )"
          icon-right="chevron-right"
          outlined
          type="is-primary"
          @click.prevent="testLostPassword(email, verificationKey, newPassword, newPasswordConfirm)"
        >
          {{ activeStep === 1 ? $t("login.finish-step") : $t("login.next-step") }}
        </b-button>
      </template>
    </b-steps>
  </Form>
</template>

<style lang="scss" scoped>
.notificationPasswordLost {
  margin: 0px;
  padding: 0.5rem;
}
/*
 * Padding reduit du bloc {@code .media} interne ( contient icone + texte ).
 * Le defaut global ( cf _common.scss ) est 1em tout autour ; ici on veut
 * un bloc plus compact pour ne pas occuper trop de hauteur verticale
 * dans une modal deja chargee .
 */
:deep(.notificationPasswordLost .media) {
  padding: 0.3em 1em;
}
/*
 * Espace le titre "Recuperation du profil" du champ Email qui suit ,
 * pour eviter qu'ils ne soient colles l'un a l'autre .
 */
.step-1-title {
  padding-bottom: 1em;
}
/*
 * Espace entre le champ Email ( step 1 ) et la barre de navigation
 * prev/next : 2em pour aerer visuellement le bas de la modal .
 */
.step-1-email-wrapper {
  margin-bottom: 2em;
}
/*
 * Lien "Renvoyer une nouvelle clé par email" affiche dans la zone
 * .help du champ verificationKey ( via slot actions de TextField ) .
 * Couleur primary , underline au hover . Disabled = grise + cursor
 * not-allowed + plus de hover pour signaler le cooldown .
 */
.resend-key-link {
  font-size: 0.85rem;
  color: var(--oa-primary, #00ae99);
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  font-weight: 500;
}
.resend-key-link:hover:not(.is-disabled) {
  text-decoration: underline;
}
.resend-key-link.is-disabled {
  color: var(--fg-muted, #999);
  cursor: not-allowed;
  pointer-events: none;
}
</style>
